import { initializeCsrfToken } from './auth';
import { getCsrfToken } from './auth';
import { captureException, performance as sentryPerformance } from './sentry';
import { notifyCalendarScopeError } from './calendarScopeError';
import i18n from '@/i18n';

/**
 * 共通ヘッダー設定
 */
const getCommonOptions = (): RequestInit => {
    // i18n の言語設定を Accept-Language ヘッダーに設定
    const language = i18n.language || 'en';

    return {
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            // セキュリティヘッダー
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            // 言語ヘッダー（バックエンドのメッセージ国際化用）
            'Accept-Language': language,
        },
    };
};

/**
 * GETリクエスト
 * @param url
 * @returns
 */
export async function apiGetRequest(url: string, extraHeaders?: Record<string, string>) {
    try {
        // リクエスト開始時刻の記録（パフォーマンス計測用）
        const markName = `api_get_${url}_start`;
        sentryPerformance.mark(markName);

        // 共通設定の取得
        const options = getCommonOptions();
        if (extraHeaders) {
            const headers = options.headers as Record<string, string>;
            Object.entries(extraHeaders).forEach(([key, value]) => {
                if (value !== undefined) {
                    headers[key] = value;
                }
            });
        }

        // APIリクエスト実行
        const response = await fetch(url, {
            ...options,
            method: 'GET',
        });

        // 403エラーはカレンダー連携のスコープ不足の可能性があるためモーダル表示
        if (response.status === 403 && url !== '/api/csrf') {
            notifyCalendarScopeError();
        }

        // パフォーマンス計測を終了
        sentryPerformance.measure(`API GET: ${url}`, markName);

        // 認証エラーの場合はログインページにリダイレクト
        if (response.status === 401) {
            window.location.replace('/login?error=unauthorized');
            throw new Error('Unauthorized');
        }

        return response;
    } catch (error) {
        // エラーをSentryに送信
        captureException(
            error instanceof Error ? error : new Error(`API GET Request failed: ${url}`),
            {
                url,
                method: 'GET',
            },
        );
        throw error;
    }
}

/**
 * POSTリクエスト
 * @param url
 * @param body リクエストボディ
 * @returns
 */
export async function apiPostRequest(url: string, body: any, extraHeaders?: Record<string, string>) {
    try {
        // リクエスト開始時刻の記録
        const markName = `api_post_${url}_start`;
        sentryPerformance.mark(markName);

        // CSRFトークンを取得
        let token = getCsrfToken();

        // トークンが空の場合は初期化を試みる
        if (!token) {
            token = await initializeCsrfToken();
        }

        // 共通設定の取得
        const options = getCommonOptions();
        if (extraHeaders) {
            const headers = options.headers as Record<string, string>;
            Object.entries(extraHeaders).forEach(([key, value]) => {
                if (value !== undefined) {
                    headers[key] = value;
                }
            });
        }

        // CSRFトークンをヘッダーに追加（トークンがある場合のみ）
        if (token) {
            (options.headers as Record<string, string>)['X-XSRF-TOKEN'] = token;
        }

        // APIリクエスト実行
        const response = await fetch(url, {
            ...options,
            method: 'POST',
            body: JSON.stringify(body),
        });

        // CSRF関連のエラーが発生した場合（403）、トークンを再取得して再試行
        if (response.status === 403 && url !== '/api/csrf') {
            // トークンを再取得
            const newToken = await initializeCsrfToken();
            if (newToken) {
                // ヘッダーを更新
                (options.headers as Record<string, string>)['X-XSRF-TOKEN'] = newToken;

                // 再試行
                const retryResponse = await fetch(url, {
                    ...options,
                    method: 'POST',
                    body: JSON.stringify(body),
                });

                // パフォーマンス計測を終了
                sentryPerformance.measure(`API POST (retry): ${url}`, markName);

                if (retryResponse.status === 403) {
                    notifyCalendarScopeError();
                }

                return retryResponse;
            }
            notifyCalendarScopeError();
        }

        // パフォーマンス計測を終了
        sentryPerformance.measure(`API POST: ${url}`, markName);

        if (response.status === 403 && url !== '/api/csrf') {
            notifyCalendarScopeError();
        }

        // 認証エラーの場合はログインページにリダイレクト
        if (response.status === 401) {
            window.location.replace('/login?error=unauthorized');
            throw new Error('Unauthorized');
        }

        return response;
    } catch (error) {
        // エラーをSentryに送信
        captureException(
            error instanceof Error ? error : new Error(`API POST Request failed: ${url}`),
            { url, method: 'POST', requestBody: JSON.stringify(body) },
        );
        throw error;
    }
}

/**
 * PUTリクエスト
 * @param url
 * @param body リクエストボディ
 * @returns
 */
export async function apiPutRequest(url: string, body: any) {
    try {
        const markName = `api_put_${url}_start`;
        sentryPerformance.mark(markName);

        let token = getCsrfToken();
        if (!token) {
            token = await initializeCsrfToken();
        }

        const options = getCommonOptions();
        if (token) {
            (options.headers as Record<string, string>)['X-XSRF-TOKEN'] = token;
        }

        const response = await fetch(url, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(body),
        });

        if (response.status === 403 && url !== '/api/csrf') {
            const newToken = await initializeCsrfToken();
            if (newToken) {
                (options.headers as Record<string, string>)['X-XSRF-TOKEN'] = newToken;
                const retryResponse = await fetch(url, {
                    ...options,
                    method: 'PUT',
                    body: JSON.stringify(body),
                });
                sentryPerformance.measure(`API PUT (retry): ${url}`, markName);
                if (retryResponse.status === 403) {
                    notifyCalendarScopeError();
                }
                return retryResponse;
            }
            notifyCalendarScopeError();
        }

        sentryPerformance.measure(`API PUT: ${url}`, markName);

        if (response.status === 403 && url !== '/api/csrf') {
            notifyCalendarScopeError();
        }

        if (response.status === 401) {
            window.location.replace('/login?error=unauthorized');
            throw new Error('Unauthorized');
        }

        return response;
    } catch (error) {
        captureException(
            error instanceof Error ? error : new Error(`API PUT Request failed: ${url}`),
            { url, method: 'PUT', requestBody: JSON.stringify(body) },
        );
        throw error;
    }
}
